import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { Crown, Sparkles, Lock, MapPin, User, ChevronDown, ChevronUp, Wand2, Copy, Send, RotateCcw } from 'lucide-react';
import IntelligenceBriefing from './IntelligenceBriefing';

const FoundersCorner = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [membersExpanded, setMembersExpanded] = useState(false);

  // AI Writer state
  const [aiTopic, setAiTopic] = useState('');
  const [aiIssueType, setAiIssueType] = useState('');
  const [aiContext, setAiContext] = useState('');
  const [aiDraft, setAiDraft] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);

  useEffect(() => {
    if (authLoading) return; // wait for auth to hydrate
    if (!user) {
      sessionStorage.setItem('redirectAfterLogin', '/founders-corner');
      navigate('/login');
      return;
    }
    fetchData();
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const res = await api.get('/founders-corner');
      setData(res.data);
    } catch (error) {
      if (error.response?.status === 403 || error.response?.status === 401) {
        setDenied(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const generatePost = async () => {
    if (!aiTopic.trim()) {
      toast.error('Please enter a topic first');
      return;
    }
    setAiGenerating(true);
    setAiDraft('');
    try {
      const res = await api.post('/founders/ai-write', {
        topic: aiTopic,
        ward: user?.ward || '',
        issue_type: aiIssueType || undefined,
        additional_context: aiContext || undefined,
      });
      setAiDraft(res.data.draft);
      toast.success('Draft generated!');
    } catch (err) {
      const msg = err.response?.data?.detail || 'AI generation failed. Try again.';
      toast.error(msg);
    } finally {
      setAiGenerating(false);
    }
  };

  const copyDraft = () => {
    if (!aiDraft) return;
    navigator.clipboard.writeText(aiDraft).then(() => toast.success('Copied to clipboard!'));
  };

  const submitDraft = () => {
    if (!aiDraft) return;
    navigate('/submit', { state: { prefillBody: aiDraft } });
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#C5A52A]"></div>
        </div>
      </Layout>
    );
  }

  if (denied || !data) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center px-4">
          <div className="max-w-md w-full text-center">
            <Lock className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h1 className="font-display text-2xl text-gray-400 mb-2">ACCESS DENIED</h1>
            <p className="text-gray-600 font-mono text-sm">
              This area is reserved for Founding Members only.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  const currentUserIndex = data.members.findIndex(m => m.id === user?.id);
  const memberNumber = currentUserIndex >= 0 ? currentUserIndex + 1 : '?';

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-black via-[#1a1a1a] to-black">
        <div className="max-w-4xl mx-auto px-4 py-16">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-block relative mb-6">
              <Crown className="w-20 h-20 text-[#C5A52A] mx-auto animate-pulse" />
              <Sparkles className="w-6 h-6 text-yellow-400 absolute -top-2 -right-2" />
              <Sparkles className="w-6 h-6 text-yellow-400 absolute -bottom-2 -left-2" />
            </div>
            <h1 className="font-headline text-6xl text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 via-[#C5A52A] to-yellow-600 mb-4">
              FOUNDER'S CORNER
            </h1>
            <p className="text-gray-400 font-mono text-sm">
              Founding Member #{memberNumber} of {data.max_supply}
            </p>
          </div>

          {/* Welcome Card */}
          <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border-2 border-[#C5A52A]/30 rounded-lg p-8 mb-8 shadow-2xl">
            <div className="flex items-start gap-4 mb-6">
              {data.badge_icon && data.badge_icon.startsWith('http') ? (
                <img
                  src={data.badge_icon}
                  alt="Founding Member"
                  className="w-24 h-24 flex-shrink-0"
                />
              ) : (
                <div className="text-6xl flex-shrink-0">{data.badge_icon || '👑'}</div>
              )}
              <div className="flex-1">
                <h2 className="font-display text-2xl text-[#C5A52A] mb-2">
                  {data.content?.welcome_title || 'YOU FOUND IT'}
                </h2>
                <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">
                  {data.content?.welcome_message ||
                    `You are one of the original ${data.max_supply} Founding Members of CYQR.CA. This exclusive space is yours to shape the future of civic engagement in Regina.`}
                </p>
              </div>
            </div>

            {/* Founder message */}
            {(data.content?.founder_message || data.content?.exclusive_updates) && (
              <div className="border-t border-[#C5A52A]/20 pt-6 space-y-4">
                {data.content?.founder_message && (
                  <div>
                    <p className="text-gray-300 leading-relaxed whitespace-pre-wrap italic">
                      "{data.content.founder_message}"
                    </p>
                    {data.content?.founder_name && (
                      <p className="text-[#C5A52A] font-display text-sm mt-2">{data.content.founder_name}</p>
                    )}
                  </div>
                )}
                {data.content?.exclusive_updates && (
                  <div className="bg-[#C5A52A]/5 border border-[#C5A52A]/20 rounded p-4">
                    <h4 className="font-display text-xs text-[#C5A52A] tracking-widest mb-2">EXCLUSIVE UPDATES</h4>
                    <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
                      {data.content.exclusive_updates}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Fallback if no admin content set */}
            {!data.content?.founder_message && !data.content?.exclusive_updates && (
              <div className="border-t border-[#C5A52A]/20 pt-6">
                <h3 className="font-display text-lg text-white mb-4">WHAT'S NEXT?</h3>
                <p className="text-gray-400 mb-4">
                  This space will evolve based on what the founding members want to see. Ideas:
                </p>
                <ul className="space-y-2 text-gray-400">
                  {['Exclusive chat room for founders', 'Early access to new features', 'Behind-the-scenes updates', 'Founder-only announcements'].map((item) => (
                    <li key={item} className="flex items-center gap-2">
                      <span className="text-[#C5A52A]">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Intelligence Briefing */}
          <div className="bg-[#0d0d0d] border border-red-900/30 rounded-lg overflow-hidden">
            <div className="flex items-center gap-3 px-6 py-4 border-b border-red-900/20 bg-red-950/10">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
              </span>
              <span className="font-mono text-xs text-red-400 tracking-[0.2em] uppercase">Intelligence Briefing</span>
              <span className="ml-auto bg-red-900/30 border border-red-800/50 text-red-400 font-mono text-[9px] tracking-widest px-2 py-0.5">FOUNDERS ONLY</span>
            </div>
            <div className="px-6 py-6">
              <IntelligenceBriefing />
            </div>
          </div>

          {/* AI Post Writer */}
          <div className="bg-[#0d0d0d] border border-[#C5A52A]/30 rounded-lg overflow-hidden">
            <div className="flex items-center gap-3 px-6 py-4 border-b border-[#C5A52A]/20 bg-[#C5A52A]/5">
              <Wand2 className="w-4 h-4 text-[#C5A52A]" />
              <span className="font-mono text-xs text-[#C5A52A] tracking-[0.2em] uppercase">AI Post Writer</span>
              <span className="ml-auto bg-[#C5A52A]/20 border border-[#C5A52A]/30 text-[#C5A52A] font-mono text-[9px] tracking-widest px-2 py-0.5">FOUNDERS ONLY</span>
            </div>
            <div className="px-6 py-6 space-y-4">
              <p className="text-gray-400 text-sm leading-relaxed">
                Describe your civic issue and let AI help you draft a punchy, shareable post. Isolated AI — no internal platform data.
              </p>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide block mb-1">What's the issue? *</label>
                <input
                  type="text"
                  value={aiTopic}
                  onChange={e => setAiTopic(e.target.value)}
                  placeholder="e.g. The pothole on 13th Ave has been there for 8 months..."
                  className="w-full bg-[#111] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-[#C5A52A]/50 focus:outline-none"
                  data-testid="ai-topic-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wide block mb-1">Issue Category</label>
                  <select
                    value={aiIssueType}
                    onChange={e => setAiIssueType(e.target.value)}
                    className="w-full bg-[#111] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white focus:border-[#C5A52A]/50 focus:outline-none"
                    data-testid="ai-issue-type"
                  >
                    <option value="">Any</option>
                    <option value="infrastructure">Infrastructure</option>
                    <option value="zoning">Zoning & Development</option>
                    <option value="spending">City Spending</option>
                    <option value="parks">Parks & Recreation</option>
                    <option value="transit">Transit</option>
                    <option value="safety">Public Safety</option>
                    <option value="environment">Environment</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wide block mb-1">Your Ward</label>
                  <input
                    type="text"
                    value={user?.ward || ''}
                    disabled
                    className="w-full bg-[#0a0a0a] border border-[#1a1a1a] rounded px-3 py-2 text-sm text-gray-500 cursor-not-allowed"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide block mb-1">Extra Context (optional)</label>
                <textarea
                  value={aiContext}
                  onChange={e => setAiContext(e.target.value)}
                  placeholder="Any specific details, dates, councillor responses, etc."
                  rows={2}
                  className="w-full bg-[#111] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-[#C5A52A]/50 focus:outline-none resize-none"
                  data-testid="ai-context-input"
                />
              </div>
              <button
                onClick={generatePost}
                disabled={aiGenerating || !aiTopic.trim()}
                data-testid="ai-generate-btn"
                className="flex items-center gap-2 px-5 py-2.5 bg-[#C5A52A] text-black font-bold rounded text-sm hover:bg-yellow-400 disabled:opacity-50 transition-colors"
              >
                <Wand2 className="w-4 h-4" />
                {aiGenerating ? 'Writing…' : 'Write My Post'}
              </button>

              {aiDraft && (
                <div className="border border-[#C5A52A]/30 rounded-lg overflow-hidden" data-testid="ai-draft-output">
                  <div className="flex items-center justify-between px-4 py-2 bg-[#C5A52A]/10 border-b border-[#C5A52A]/20">
                    <span className="text-xs text-[#C5A52A] font-mono uppercase tracking-wide">Your Draft</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => { setAiDraft(''); setAiTopic(''); setAiContext(''); }}
                        className="text-gray-500 hover:text-gray-300 p-1"
                        title="Clear"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={copyDraft}
                        data-testid="copy-draft-btn"
                        className="flex items-center gap-1 text-xs text-gray-400 hover:text-white px-2 py-1 bg-[#1a1a1a] rounded"
                      >
                        <Copy className="w-3.5 h-3.5" /> Copy
                      </button>
                      <button
                        onClick={submitDraft}
                        data-testid="submit-draft-btn"
                        className="flex items-center gap-1 text-xs text-black font-bold px-2 py-1 bg-[#C5A52A] rounded hover:bg-yellow-400"
                      >
                        <Send className="w-3.5 h-3.5" /> Submit
                      </button>
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="text-gray-200 text-sm leading-relaxed whitespace-pre-wrap">{aiDraft}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Founding Members List - Collapsible */}
          <div className="bg-[#111] border border-[#1a1a1a] rounded-lg">
            <button
              onClick={() => setMembersExpanded(prev => !prev)}
              className="w-full flex items-center justify-between p-6 text-left hover:bg-[#141414] transition-colors rounded-lg"
            >
              <div className="flex items-center gap-3">
                <h3 className="font-display text-xl text-[#C5A52A]">THE FOUNDING {data.max_supply}</h3>
                <span className="font-mono text-xs text-gray-500">
                  {data.member_count}/{data.max_supply} CLAIMED
                </span>
              </div>
              {membersExpanded
                ? <ChevronUp className="w-5 h-5 text-[#C5A52A]" />
                : <ChevronDown className="w-5 h-5 text-[#C5A52A]" />}
            </button>

            {membersExpanded && (
              <div className="px-6 pb-6">
                {data.members.length === 0 ? (
                  <p className="text-gray-500 text-sm font-mono text-center py-8">
                    Founding members will appear here as they are awarded...
                  </p>
                ) : (
                  <div className="space-y-3">
                    {data.members.map((member, index) => (
                      <div
                        key={member.id || index}
                        className={`flex items-center gap-4 p-3 rounded transition-colors ${
                          member.id === user?.id
                            ? 'bg-[#C5A52A]/10 border border-[#C5A52A]/30'
                            : 'bg-[#0a0a0a] hover:bg-[#141414]'
                        }`}
                        data-testid={`founder-member-${index}`}
                      >
                        <span className="font-mono text-xs text-[#C5A52A]/60 w-6 text-right flex-shrink-0">
                          {index + 1}
                        </span>
                        {member.profile_image_url ? (
                          <img
                            src={member.profile_image_url}
                            alt={member.name}
                            className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-8 h-8 bg-[#1a1a1a] rounded-full flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4 text-gray-600" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <span className="font-display text-sm text-white">
                            {member.name?.toUpperCase()}
                            {member.id === user?.id && (
                              <span className="ml-2 text-[#C5A52A] text-xs">(YOU)</span>
                            )}
                          </span>
                          {member.ward && (
                            <span className="flex items-center gap-1 text-[10px] font-mono text-gray-600 mt-0.5">
                              <MapPin className="w-3 h-3" /> {member.ward}
                            </span>
                          )}
                        </div>
                        <span className="font-mono text-[10px] text-gray-600 flex-shrink-0">
                          {formatDate(member.joined_at)}
                        </span>
                      </div>
                    ))}
                    {Array.from({ length: Math.max(0, data.max_supply - data.member_count) }).map((_, i) => (
                      <div key={`empty-${i}`} className="flex items-center gap-4 p-3 rounded bg-[#0a0a0a] opacity-30">
                        <span className="font-mono text-xs text-gray-600 w-6 text-right">{data.member_count + i + 1}</span>
                        <div className="w-8 h-8 border border-dashed border-gray-700 rounded-full" />
                        <span className="font-mono text-xs text-gray-600">UNCLAIMED</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default FoundersCorner;
